<!-- HrTimelineChart — HR over time with stroke-color band strip.
     Chart logic extracted from P2HrTimeline; proto chrome stripped.
     Sub-15px axis/strip labels are an accepted chart-geometry constraint
     (clock-time ticks at 15px overlap on a 390px canvas; strip labels only
     render on bands >50px and must fit the 16px strip height). -->
<script lang="ts">
  import { onMount } from "svelte";
  import echarts from "../../lib/echarts";
  import { mountChart, darkTooltip } from "../../lib/echartsHelpers";
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { strokeColor, COLORS } from "../../lib/designTokens";
  import { hrStripCaption } from "../../lib/openWaterCopy";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  let el: HTMLDivElement;

  // The band paints ONE color per set = that set's MAIN (most-swum) stroke, not
  // its first lap. First-lap mispainted set 0 of the Jul 9 swim (1 breaststroke
  // lead + 7 freestyle) entirely teal; and it's why Backstroke — genuinely never
  // any set's main stroke on this swim, only scattered mid-set inside 3 mixed
  // sets — never surfaced while the same-count Butterfly (which DOES lead sets)
  // did. Dominant-stroke makes the band honest to its caption ("each set's main
  // stroke"); the donut and per-length table still count every length, including
  // those backstroke ones, at length granularity (persona gate final, B1).
  function mainStroke(laps: { strokeStyle: string }[]): string {
    const counts = new Map<string, number>();
    for (const l of laps) counts.set(l.strokeStyle, (counts.get(l.strokeStyle) ?? 0) + 1);
    // Laps are in time order, so on a tie the earliest-appearing stroke wins
    // (stable, matches the set's opening for even IM-style sets like the Rockalita).
    let best = laps[0]?.strokeStyle ?? "Unknown";
    let bestN = -1;
    for (const [st, n] of counts) {
      if (n > bestN) { bestN = n; best = st; }
    }
    return best;
  }

  // Sets with enough lengths to show as a band segment (skip tiny 1-length
  // anomalies and unknown-only sets), each colored by its main stroke.
  let bandSegments = $derived(
    dw.setGroups
      .filter((s) => s.laps.length >= 2)
      .map((s) => ({ startMs: s.startMs, endMs: s.endMs, stroke: mainStroke(s.laps) }))
      .filter((b) => b.stroke !== "Unknown"),
  );

  onMount(() => {
    const hrData = dw.hrSeries.map((p) => [p.ts, p.bpm]);

    // Rest-interval shaded background bands — the "I took a break here" story.
    // Open water: the HR-valley pass marks low-effort stretches (wade-in,
    // sighting), not wall rests — "easy" is the honest word there (B7).
    const restWord = dw.isOpenWater ? "easy" : "rest";
    const restBands = dw.restIntervals.map((r) => [
      { xAxis: r.startMs, itemStyle: { color: "rgba(183, 199, 232, 0.10)" }, name: `${Math.round(r.durationSeconds)}s ${restWord}` },
      { xAxis: r.endMs },
    ]);

    // Stroke-set bands rendered as a thin colored strip ALONG THE TOP of the chart.
    // No per-chip text labels — the strip is a color timeline only. A caption
    // beneath the chart provides context ("Sets across this swim").
    //
    // The strip shares the HR line's yAxis (`scale: true`). ECharts auto-scales
    // that axis from the y-ENCODED dimension of every series. So index 1 must be
    // a small constant (stripY), NOT a millisecond timestamp — otherwise the ms
    // value hijacks the BPM extent and flattens the HR line to zero. The
    // explicit `encode` pins x to the start/end ms columns and y to stripY.
    const stripY = 1; // constant; never drives the BPM axis extent
    const stripSeries = {
      type: "custom" as const,
      // tuple: [startMs, stripY, endMs, mainStroke]
      data: bandSegments.map((b) => [b.startMs, stripY, b.endMs, b.stroke]),
      encode: { x: [0, 2], y: 1 },
      renderItem: (_params: unknown, api: {
        coord: (pt: [number, number]) => [number, number];
        value: (idx: number) => number | string;
      }) => {
        const start = api.coord([api.value(0) as number, 0]);
        const end = api.coord([api.value(2) as number, 0]);
        const stroke = api.value(3) as string;
        const width = end[0] - start[0];
        return {
          type: "rect",
          shape: { x: start[0], y: 0, width: width, height: 16 },
          style: { fill: strokeColor(stroke), opacity: 0.85 },
        };
      },
    };

    return mountChart(el, (chart) => chart.setOption({
      backgroundColor: "transparent",
      grid: { left: 40, right: 12, top: 26, bottom: 28 },
      tooltip: {
        trigger: "axis",
        ...darkTooltip((params: { value: [number, number]; seriesIndex: number }[]) => {
          const hrPoint = params.find((p) => p.seriesIndex === 0);
          if (!hrPoint) return "";
          const d = new Date(hrPoint.value[0]);
          return `${d.toLocaleTimeString()}<br/><strong>${(hrPoint.value[1] as number).toFixed(0)} bpm</strong>`;
        }),
      },
      xAxis: {
        type: "time",
        axisLine: { lineStyle: { color: COLORS.navyRaised } },
        axisLabel: {
          color: COLORS.textDim,
          // 10px is intentional: clock-time ticks at 15px overlap badly on a
          // 390px canvas. Accepted chart-geometry exception.
          fontSize: 10,
          hideOverlap: true,
          formatter: (v: number) => {
            const d = new Date(v);
            return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
          },
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLine: { show: false },
        // 10px y-axis ticks: same reason as x-axis. Accepted exception.
        axisLabel: { color: COLORS.textDim, fontSize: 10 },
        splitLine: { lineStyle: { color: COLORS.navyRaised, opacity: 0.3 } },
      },
      series: [
        {
          name: "Heart rate",
          type: "line",
          data: hrData,
          smooth: 0.3,
          showSymbol: false,
          lineStyle: { color: COLORS.coralRed, width: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(255,107,107,0.30)" },
              { offset: 1, color: "rgba(255,107,107,0)" },
            ]),
          },
          markArea: {
            silent: true,
            // In-place label so the dashed block reads as intentional, not a
            // render glitch (persona gate 2026-07-11, B7 — 4/5 saw a "broken
            // placeholder" before finding the caption two sections down).
            label: {
              show: true,
              // Offset down-right out of the top-left corner: at insideTop the
              // label stacked onto the topmost y-axis tick ("210") and read as
              // one garbled string (persona gate 2026-07-11 round 2, B1, 5/5).
              // The early HR line runs low, so the upper plot area is clear.
              position: "insideTop" as const,
              offset: [22, 40],
              color: COLORS.textDim,
              fontSize: 10,
              // Chip treatment: y-ticks repeat every ~53px so EVERY vertical
              // offset shares a baseline with some tick number — the pill
              // background detaches the label visually instead of dodging.
              backgroundColor: "rgba(11, 16, 32, 0.85)",
              padding: [3, 6],
              borderRadius: 4,
              formatter: (p: { name: string }) => p.name,
            },
            itemStyle: { borderColor: "rgba(183,199,232,0.4)", borderWidth: 1, borderType: "dashed" as const },
            data: restBands,
          },
        },
        stripSeries,
      ],
    }));
  });
</script>

{#if dw.hrSeries.length === 0}
  <p class="no-hr">No heart rate data in this swim.</p>
{:else}
  <div class="chart" bind:this={el}></div>
  <p class="strip-caption">{hrStripCaption(dw.isOpenWater, dw.restIntervals.length)}</p>
  <div class="readout">
    <span class="stat"><strong>{dw.avgHr?.toFixed(0) ?? "no data"}</strong> <span class="stat-label">avg bpm</span></span>
    <span class="stat"><strong>{dw.maxHr?.toFixed(0) ?? "no data"}</strong> <span class="stat-label">peak bpm</span></span>
  </div>
{/if}

<style>
  .chart { width: 100%; height: 240px; }
  .no-hr { font-size: 15px; color: var(--text-muted); text-align: center; padding: 24px 0; margin: 0; }

  /* One caption beneath the colored strip — replaces per-chip text labels. */
  .strip-caption {
    font-size: 15px;
    color: var(--text-muted);
    margin: 2px 0 6px;
    text-align: left;
    padding-left: 40px; /* align with chart grid left offset */
  }

  /* Primary glance-read: avg/peak BPM at 15px+ floor. */
  .readout {
    display: flex;
    gap: 1.25rem;
    margin-top: 0.5rem;
    align-items: baseline;
  }
  .stat { display: inline-flex; align-items: baseline; gap: 0.3rem; }
  .stat strong {
    font-size: 22px;
    color: var(--accent-fire);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
  }
  .stat-label { font-size: 15px; font-weight: 600; color: var(--text-primary); }
</style>
